import { z } from "zod";

/**
 * Chat for Cede Arena. One shared room per competition (`campaignName` + `epochId`)
 * plus one lobby-wide GLOBAL room. Public read, authenticated write. A single
 * message entity carries all three input modes via the `kind` discriminator:
 * - FREE    — free-text message (`text` set)
 * - PRESET  — one of the curated short messages (`presetId` + resolved `text`)
 * - REACTION — a single curated emoji (`emoji` set)
 *
 * The allowed preset ids and reaction emojis below are the single source of
 * truth for both FE rendering and BE validation. Bump here, redeploys both.
 */

export const ARENA_CHAT_MAX_TEXT_LENGTH = 200;

/**
 * Grace window after a competition's epoch ends during which participants can
 * still post to the chat. After `epoch.end + this`, sending is disabled (the
 * backend rejects it and the composer is locked). Shared so the API rule and the
 * arena UI stay in lockstep.
 */
export const ARENA_CHAT_SEND_GRACE_MS = 5 * 60 * 1000;

/** How long before the start a competition's chat opens with `pre_start_chat_enable` on; the arena's copy reads it too. */
export const ARENA_CHAT_PRE_START_OPEN_MS = 30 * 60 * 1000;

export const ArenaChatRoomTypeSchema = z.enum(["DUEL", "GLOBAL"]);
export type ArenaChatRoomType = z.infer<typeof ArenaChatRoomTypeSchema>;

// Link-bait TLDs that count on a bare domain on their own.
const ARENA_CHAT_BARE_DOMAIN_TLDS = [
  "com",
  "net",
  "org",
  "io",
  "gg",
  "xyz",
  "co",
  "app",
  "dev",
  "ai",
  "finance",
  "trade",
  "exchange",
  "info",
  "biz",
  "ru",
  "de",
  "fr",
  "uk",
  "tv",
  "ly",
  "sh",
  "link",
  "click",
  "site",
  "online",
  "store",
  "vip",
  "pro",
  "club",
  "cc",
  "eu",
  "money",
  "cash",
] as const;

// English-word TLDs count on a bare domain only before a `/` path, so "nice.me next" passes but "t.me/pump" does not.
const ARENA_CHAT_WORD_TLDS = [
  "so",
  "in",
  "to",
  "me",
  "us",
  "live",
  "win",
  "is",
  "it",
  "at",
  "be",
  "do",
  "go",
  "no",
  "my",
  "on",
  "one",
  "now",
  "fun",
  "run",
  "top",
  "bet",
] as const;

/**
 * A scheme or `www.` prefix counts with any TLD; a bare domain with a link-bait TLD, or a word TLD followed by a path.
 * The prefix starts a match only at the first label of a dotted/hyphenated run, keeping it linear. It is consumed
 * rather than a lookbehind, which Safari before 16.4 cannot parse; this module loads in every app's bundle.
 * No `g` flag: a global regex keeps `lastIndex` between `.test` calls.
 */
export const ARENA_CHAT_LINK_PATTERN = new RegExp(
  String.raw`(?:https?:\/\/|www\.)\S|(?:^|[^a-z0-9.-]|(?:^|[^a-z0-9-])\.)[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:(?:${ARENA_CHAT_BARE_DOMAIN_TLDS.join("|")})\b|(?:${ARENA_CHAT_WORD_TLDS.join("|")})\/)`,
  "i",
);

export const ARENA_CHAT_LINK_ERROR_MESSAGE = "Links aren't allowed in chat";

/** Error codes the chat endpoints return in the RFC 9457 `type` field. The API's `ArenaChatErrorCode` derives from this. */
export const ARENA_CHAT_ERROR_CODE = {
  ARENA_CHAT_INVALID_CONTENT: "ARENA_CHAT_INVALID_CONTENT",
  ARENA_CHAT_ROOM_NOT_LIVE: "ARENA_CHAT_ROOM_NOT_LIVE",
  ARENA_CHAT_NOT_PARTICIPANT: "ARENA_CHAT_NOT_PARTICIPANT",
  ARENA_CHAT_RATE_LIMITED: "ARENA_CHAT_RATE_LIMITED",
  ARENA_CHAT_DISABLED: "ARENA_CHAT_DISABLED",
  ARENA_CHAT_MODERATION_FAILED: "ARENA_CHAT_MODERATION_FAILED",
  ARENA_CHAT_BOT_GENERATION_FAILED: "ARENA_CHAT_BOT_GENERATION_FAILED",
  ARENA_CHAT_WRONG_ROOM: "ARENA_CHAT_WRONG_ROOM",
  ARENA_CHAT_LINK_NOT_ALLOWED: "ARENA_CHAT_LINK_NOT_ALLOWED",
  ARENA_CHAT_NICKNAME_REQUIRED: "ARENA_CHAT_NICKNAME_REQUIRED",
  ARENA_CHAT_GLOBAL_DISABLED: "ARENA_CHAT_GLOBAL_DISABLED",
} as const;
export type ARENA_CHAT_ERROR_CODE = (typeof ARENA_CHAT_ERROR_CODE)[keyof typeof ARENA_CHAT_ERROR_CODE];

/** Folds what still pastes as a link: compatibility forms (`．`), invisible characters (U+200B, U+3164), ideographic stops. */
const foldForLinkCheck = (text: string): string =>
  text
    .normalize("NFKC")
    .replace(/\p{Default_Ignorable_Code_Point}/gu, "")
    .replace(/[\u3002\uFF61]/g, ".");

/** Over-length text is rejected by the length cap, so the regex never scans an unbounded body. */
export const containsArenaChatLink = (text: string): boolean =>
  text.length <= ARENA_CHAT_MAX_TEXT_LENGTH && ARENA_CHAT_LINK_PATTERN.test(foldForLinkCheck(text));

export const ArenaChatMessageKindSchema = z.enum(["FREE", "PRESET", "REACTION"]);
export type ArenaChatMessageKind = z.infer<typeof ArenaChatMessageKindSchema>;

/** Why a message was banned. `AI_REPORT` = flagged by the async AI moderation job. */
export const ArenaChatBanReasonSchema = z.enum(["AI_REPORT"]);
export type ArenaChatBanReason = z.infer<typeof ArenaChatBanReasonSchema>;

/** Curated preset messages. `id` is persisted + validated; `text` is rendered. */
export const ARENA_CHAT_PRESETS = [
  { id: "gg", text: "GG!" },
  { id: "good_luck", text: "Good luck!" },
  { id: "nice_trade", text: "Nice trade!" },
  { id: "lets_go", text: "Let's go!" },
  { id: "too_easy", text: "Too easy 😎" },
  { id: "watch_this", text: "Watch this!" },
  { id: "not_over", text: "It's not over!" },
  { id: "well_played", text: "Well played!" },
] as const;

export type ArenaChatPresetId = (typeof ARENA_CHAT_PRESETS)[number]["id"];
export const ARENA_CHAT_PRESET_IDS = ARENA_CHAT_PRESETS.map((preset) => preset.id) as [
  ArenaChatPresetId,
  ...ArenaChatPresetId[],
];

/**
 * Colon-token custom emote (Slack/Discord convention) rendered as the Cede
 * Arena logo mark instead of a unicode emoji. Persisted in the same `emoji`
 * column as the unicode reactions.
 */
export const ARENA_CHAT_ARENA_LOGO_REACTION = ":arena:";

/**
 * Every reaction a message may carry (the send allow-list, validated
 * server-side): the `:arena:` logo emote + the full curated emoji catalogue
 * shown in the composer's reaction picker. Grouped in the same order as
 * {@link ARENA_CHAT_REACTION_CATEGORIES}.
 */
export const ARENA_CHAT_REACTIONS = [
  ARENA_CHAT_ARENA_LOGO_REACTION,
  "🔥",
  "⚔️",
  "🏆",
  "🥇",
  "🥈",
  "🥉",
  "🏅",
  "👑",
  "🎯",
  "🏁",
  "⚡",
  "💥",
  "💣",
  "🧨",
  "🛡️",
  "💎",
  "🚀",
  "📈",
  "📉",
  "📊",
  "💰",
  "🤑",
  "💸",
  "🪙",
  "🎰",
  "🎲",
  "⏳",
  "🧂",
  "🐳",
  "🐻",
  "🐂",
  "🦍",
  "🐸",
  "😀",
  "😁",
  "😄",
  "😆",
  "😅",
  "😂",
  "🤣",
  "🙃",
  "😉",
  "😊",
  "😇",
  "😋",
  "😛",
  "😜",
  "🤪",
  "🥲",
  "😍",
  "🤩",
  "😌",
  "😏",
  "😎",
  "🤓",
  "🥳",
  "🤗",
  "🤔",
  "🤫",
  "🫡",
  "🫣",
  "🙄",
  "😐",
  "😑",
  "😶",
  "😒",
  "😔",
  "😟",
  "😬",
  "😴",
  "🥱",
  "🤯",
  "😮",
  "😲",
  "😳",
  "🥺",
  "😢",
  "😭",
  "😱",
  "😤",
  "😡",
  "🤬",
  "🥵",
  "🥶",
  "🥴",
  "😵",
  "😵‍💫",
  "🫠",
  "😈",
  "👿",
  "💀",
  "🤡",
  "👻",
  "🤖",
  "👍",
  "👎",
  "👏",
  "🙌",
  "🫶",
  "🤝",
  "💪",
  "✌️",
  "🤞",
  "🫰",
  "👌",
  "🤌",
  "🤙",
  "👋",
  "✋",
  "👊",
  "🙏",
  "💩",
  "👀",
  "❤️",
  "💔",
  "🖤",
  "💯",
  "✨",
  "🎉",
  "🎊",
  "🍀",
  "🗿",
] as const;
export type ArenaChatReaction = (typeof ARENA_CHAT_REACTIONS)[number];

/** Subset pinned in the composer's one-tap shortcut bar. */
export const ARENA_CHAT_REACTION_SHORTCUTS = [
  ARENA_CHAT_ARENA_LOGO_REACTION,
  "🔥",
  "😂",
  "😮",
  "😭",
  "👏",
  "💀",
  "⚔️",
  "💩",
] as const satisfies ReadonlyArray<ArenaChatReaction>;

/**
 * The full catalogue partitioned for the picker's grouped display. Must cover
 * {@link ARENA_CHAT_REACTIONS} exactly (asserted by the arena test suite).
 */
export const ARENA_CHAT_REACTION_CATEGORIES = [
  {
    id: "arena",
    label: "Arena",
    reactions: [
      ARENA_CHAT_ARENA_LOGO_REACTION,
      "🔥",
      "⚔️",
      "🏆",
      "🥇",
      "🥈",
      "🥉",
      "🏅",
      "👑",
      "🎯",
      "🏁",
      "⚡",
      "💥",
      "💣",
      "🧨",
      "🛡️",
      "💎",
      "🚀",
      "📈",
      "📉",
      "📊",
      "💰",
      "🤑",
      "💸",
      "🪙",
      "🎰",
      "🎲",
      "⏳",
      "🧂",
      "🐳",
      "🐻",
      "🐂",
      "🦍",
      "🐸",
    ],
  },
  {
    id: "smileys",
    label: "Smileys",
    reactions: [
      "😀",
      "😁",
      "😄",
      "😆",
      "😅",
      "😂",
      "🤣",
      "🙃",
      "😉",
      "😊",
      "😇",
      "😋",
      "😛",
      "😜",
      "🤪",
      "🥲",
      "😍",
      "🤩",
      "😌",
      "😏",
      "😎",
      "🤓",
      "🥳",
      "🤗",
      "🤔",
      "🤫",
      "🫡",
      "🫣",
      "🙄",
      "😐",
      "😑",
      "😶",
      "😒",
      "😔",
      "😟",
      "😬",
      "😴",
      "🥱",
      "🤯",
      "😮",
      "😲",
      "😳",
      "🥺",
      "😢",
      "😭",
      "😱",
      "😤",
      "😡",
      "🤬",
      "🥵",
      "🥶",
      "🥴",
      "😵",
      "😵‍💫",
      "🫠",
      "😈",
      "👿",
      "💀",
      "🤡",
      "👻",
      "🤖",
    ],
  },
  {
    id: "gestures",
    label: "Gestures",
    reactions: ["👍", "👎", "👏", "🙌", "🫶", "🤝", "💪", "✌️", "🤞", "🫰", "👌", "🤌", "🤙", "👋", "✋", "👊", "🙏"],
  },
  {
    id: "more",
    label: "More",
    reactions: ["💩", "👀", "❤️", "💔", "🖤", "💯", "✨", "🎉", "🎊", "🍀", "🗿"],
  },
] as const satisfies ReadonlyArray<{ id: string; label: string; reactions: ReadonlyArray<ArenaChatReaction> }>;

const ArenaChatMessageBaseSchema = z.object({
  id: z.number().int().positive(),
  /** Author's Hub user id. */
  hubUserId: z.number().int().positive(),
  /** User-set Arena username (`HubUser.nickname`). `null` falls back to `Player #<hubUserId>`. */
  nickname: z.string().nullable(),
  kind: ArenaChatMessageKindSchema,
  /** FREE/PRESET: rendered text. REACTION: null. */
  text: z.string().nullable(),
  /** REACTION: emoji. else null. */
  emoji: z.string().nullable(),
  /** PRESET: preset id. else null. */
  presetId: z.string().nullable(),
  /** Set when moderation banned the message: `text`/`emoji` are nulled everywhere it is served. */
  bannedReason: ArenaChatBanReasonSchema.nullable(),
  /** Epoch milliseconds. */
  createdAt: z.number().int().nonnegative(),
});

export const ArenaDuelChatMessageSchema = ArenaChatMessageBaseSchema.extend({
  // Default: payloads written before rooms existed were all duel messages.
  roomType: z.literal("DUEL").default("DUEL"),
  campaignName: z.string(),
  epochId: z.number().int().nonnegative(),
});
export type ArenaDuelChatMessageDto = z.infer<typeof ArenaDuelChatMessageSchema>;

export const ArenaGlobalChatMessageSchema = ArenaChatMessageBaseSchema.extend({
  roomType: z.literal("GLOBAL"),
  campaignName: z.null(),
  epochId: z.null(),
});
export type ArenaGlobalChatMessageDto = z.infer<typeof ArenaGlobalChatMessageSchema>;

export const ArenaChatMessageSchema = z.union([ArenaDuelChatMessageSchema, ArenaGlobalChatMessageSchema]);
export type ArenaChatMessageDto = z.infer<typeof ArenaChatMessageSchema>;

/**
 * Payload of the `moderation` SSE event pushed on the room stream when the AI
 * moderation job bans a message: clients replace the message in place with a
 * removal notice.
 */
export const ArenaChatModerationEventSchema = z.object({
  id: z.number().int().positive(),
  campaignName: z.string(),
  epochId: z.number().int().nonnegative(),
  bannedReason: ArenaChatBanReasonSchema,
});
export type ArenaChatModerationEvent = z.infer<typeof ArenaChatModerationEventSchema>;

/** GLOBAL-room twin of {@link ArenaChatModerationEventSchema}, pushed on the notifications SSE. */
export const ArenaGlobalChatModerationEventSchema = z.object({
  id: z.number().int().positive(),
  roomType: z.literal("GLOBAL"),
  bannedReason: ArenaChatBanReasonSchema,
});
export type ArenaGlobalChatModerationEvent = z.infer<typeof ArenaGlobalChatModerationEventSchema>;

/** Query params identifying a chat room (campaign + epoch). `epoch` arrives as a string. */
export const ArenaChatRoomQuerySchema = z.object({
  campaign: z.string().min(1),
  epoch: z.coerce.number().int().nonnegative(),
});
export type ArenaChatRoomQuery = z.infer<typeof ArenaChatRoomQuerySchema>;

/** Query for `GET /v1/cede-arena-chat/messages` — room + optional page size (capped server-side). */
export const GetArenaChatHistoryQuerySchema = ArenaChatRoomQuerySchema.extend({
  limit: z.coerce.number().int().positive().optional(),
});
export type GetArenaChatHistoryQueryParams = z.infer<typeof GetArenaChatHistoryQuerySchema>;

/** Response payload for `GET /v1/cede-arena-chat/messages`. Ascending by `createdAt`. */
export const GetArenaChatHistoryResponseSchema = z.object({
  messages: ArenaChatMessageSchema.array(),
});
export type GetArenaChatHistoryResponse = z.infer<typeof GetArenaChatHistoryResponseSchema>;

export const ArenaChatFreeTextSchema = z
  .string()
  .trim()
  .min(1)
  .max(ARENA_CHAT_MAX_TEXT_LENGTH)
  // Zod still runs refinements after `.max` fails; skip the link check so the length issue stands alone.
  .refine((text) => text.length > ARENA_CHAT_MAX_TEXT_LENGTH || !containsArenaChatLink(text), {
    error: ARENA_CHAT_LINK_ERROR_MESSAGE,
  });

const sendRoomFields = {
  roomType: ArenaChatRoomTypeSchema.default("DUEL"),
  campaignName: z.string().min(1).optional(),
  epochId: z.number().int().nonnegative().optional(),
};

/**
 * `POST /v1/cede-arena-chat/send`: DUEL names its room (campaign + epoch), GLOBAL none; the backend re-validates content.
 */
export const SendArenaChatMessageRequestSchema = z
  .discriminatedUnion("kind", [
    z.object({ kind: z.literal("FREE"), ...sendRoomFields, text: ArenaChatFreeTextSchema }),
    z.object({ kind: z.literal("PRESET"), ...sendRoomFields, presetId: z.enum(ARENA_CHAT_PRESET_IDS) }),
    z.object({ kind: z.literal("REACTION"), ...sendRoomFields, emoji: z.enum(ARENA_CHAT_REACTIONS) }),
  ])
  .superRefine((body, ctx) => {
    if (body.roomType === "DUEL" && (body.campaignName == null || body.epochId == null)) {
      ctx.addIssue({
        code: "custom",
        message: "A DUEL message needs campaignName and epochId",
        path: [body.campaignName == null ? "campaignName" : "epochId"],
      });
    }
    if (body.roomType === "GLOBAL" && (body.campaignName != null || body.epochId != null)) {
      ctx.addIssue({ code: "custom", message: "A GLOBAL message has no campaignName or epochId", path: ["roomType"] });
    }
  });
export type SendArenaChatMessageRequest = z.infer<typeof SendArenaChatMessageRequestSchema>;

/**
 * Send is fire-and-broadcast: the command persists + publishes, and the
 * message reaches every client (the sender included) via the room SSE stream,
 * so the POST only acks. Keeps the controller CQRS-pure (command → void).
 */
export const SendArenaChatMessageResponseSchema = z.object({
  accepted: z.literal(true),
});
export type SendArenaChatMessageResponse = z.infer<typeof SendArenaChatMessageResponseSchema>;
